import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import useRefsInit from "../hooks/useRefsInit.jsx";
import BryntumContext, { userPreferences } from "../context/bryntumContext.jsx";
import BryntumGridWrapper from "../components/BryntumGrid.jsx";
import { callOnLoadOnColumns } from "../../other/utils/otherUtils.jsx";
import { getOriginalRecord, swivuCheckGridGrants } from "../../other/utils/utils.jsx";
import AuthContext from "../context/authContext.jsx";
import { processCellMenuItems } from "../../other/utils/gridCellMenuUtils.jsx";
import AlertNotificationBanner from "../components/alertNotificationBanner/AlertNotificationBanner.jsx";
import { EDITOR_KEYS } from "../../other/constants/EditorKeys.jsx";
import useEnableViewTracking from "../hooks/useEnableViewTracking.jsx";
import { VIEW_TYPES } from "../../other/constants/OtherKeys.jsx";
import { STORE_KEYS } from "../../other/constants/StoreKeys.jsx";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";
import ViewSpecificHeader from "../components/appHeader/ViewSpecificHeader.jsx";
import { useSwivuKanban } from "../hooks/useSwivuKanban";
import { KanbanWrapper } from "@/modern/components/kanban-wrapper";
import { UserAvatar } from "@/modern/components/user-avatar";
import { Badge } from "@/modern/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import { CopyToBoardDialog } from "@/react/components/CopyToBoardDialog";

const TaskPage = () => {
  const gridRef = useRef(null);
  const { setRefs, isRefsInit } = useRefsInit([gridRef]);
  const { user } = useContext(AuthContext);
  const { project, editorHandler, currentViewRef, stateProvider, entityManager, viewManager } = useContext(BryntumContext);
  useEnableViewTracking(gridRef, VIEW_TYPES.tasks, isRefsInit);
  const kanban = useSwivuKanban({ gridRef, storeId: STORE_KEYS.taskStore, entityType: ENTITY_TYPES.TASK });
  const [bulkCopyRecords, setBulkCopyRecords] = useState(null);

  // local state for rowHeight
  const editorHandlerRef = useRef();
  editorHandlerRef.current = editorHandler;

  // install listener on userPreferences
  useEffect(() => {
    userPreferences.boardPreferencesStore.on(
      "change",
      ({ record }) => {
        if (record?.key === "taskStyle" || record?.key === "taskColor") {
          gridRef.current.instance.refreshRows();
        }
      },
      gridRef.current.instance
    );
  }, []);

  useEffect(() => {
    if (project.extraTaskStore) {
      gridRef.current.instance.store = project.extraTaskStore;
      gridRef.current.instance.store.relayAll(gridRef.current.instance, "swivuTaskStore");
      gridRef.current.instance.features.cellMenu.processItems = processCellMenuItems({
        editorHandler,
        grid: gridRef.current.instance,
        entityName: "Task",
        promptOnRemove: true,
        project,
        tagStoreKey: STORE_KEYS.taskTagValuesStore,
        onBulkCopyTo: (selectedRecords) => setBulkCopyRecords(selectedRecords),
      });
      gridRef.current.instance.onClickSwivuEditMessage = () => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: "Settings" });
      };
      swivuCheckGridGrants(gridRef.current.instance, user);
      callOnLoadOnColumns(gridRef.current.instance.columns, project, { editorHandler, stateProvider, currentViewRef, user, entityManager, viewManager });
    }
  }, [project, user, stateProvider, editorHandler, viewManager]);

  /**
   * Get all board users (from resources) that are assigned to events of this task
   * Similar to how it's done in SwivuSchedulerPro.jsx and SwivuPointsLeaderboardPopup.jsx
   * @param {Object} taskRecord - The task record
   * @returns {Array} Array of unique board users
   */
  const getTaskBoardUsers = useCallback((taskRecord) => {
    if (!project || !taskRecord) return [];

    const taskEvents = project.eventStore.query((eventRecord) => eventRecord.taskId === taskRecord.id, true);
    if (!taskEvents || taskEvents.length === 0) return [];

    const boardUsersMap = new Map();
    taskEvents.forEach((event) => {
      const resource = event.resource;
      if (resource && resource._assignedBoardUsers) {
        resource._assignedBoardUsers.forEach((boardUser) => {
          if (boardUser && !boardUsersMap.has(boardUser.id)) {
            boardUsersMap.set(boardUser.id, {
              id: boardUser.id,
              name: boardUser.name,
              profileImageURL: boardUser.profileImageURL,
              archived: boardUser.archived
            });
          }
        });
      }
    });
    return Array.from(boardUsersMap.values());
  }, [project]);

  const kanbanItemRenderer = useCallback(
    (record) => {
      const boardUsers = getTaskBoardUsers(record);
      const activeConfig = kanban?.getActiveConfig?.(project?.extraTaskStore);
      const value = record?.[activeConfig?.grouperField];
      const badgeClassName = "text-xs px-1.5 py-0.5 rounded-md truncate text-start min-w-0 truncate inline-block";
      return (
        <div className="flex-1 min-w-0">
          <div
            style={{
              width: "0.25rem",
              height: "100%",
              backgroundColor: record.color,
              position: "absolute",
              top: "0",
              left: "0"
            }}
          />
          <div className="flex items-start justify-between gap-2 mb-2">
            <h4 className={cn("text-sm font-medium leading-tight text-foreground line-clamp-2", record?.archived && "line-through")}>{record.name || "Untitled Task"}</h4>
          </div>
          {record.description && <p className="text-xs text-muted-foreground mb-3 line-clamp-2 leading-relaxed m-0">{record.description}</p>}
          <div className="flex items-center justify-between gap-2 min-w-0">
            {Array.isArray(value) ? (
              <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
                {value.length > 0 ? (
                  <>
                    {value.slice(0, 2).map((v, i) => (
                      <Badge key={i} variant="accent" size="sm" className={badgeClassName}>
                        {v}
                      </Badge>
                    ))}
                    {value.length > 2 && (
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <span className="text-xs text-muted-foreground shrink-0 cursor-default">+{value.length - 2}</span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p className="font-medium mb-1">More tags</p>
                          <ul className="text-xs list-disc list-inside space-y-0.5">
                            {value.slice(2).map((v, i) => (
                              <li key={i}>{v}</li>
                            ))}
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </>
                ) : (
                  <Badge variant="accent" size="sm" className={badgeClassName}>N/A</Badge>
                )}
              </div>
            ) : (
              <Badge variant="accent" size="sm" className={badgeClassName}>
                {value || "N/A"}
              </Badge>
            )}
            {boardUsers.length > 0 && (
              <div className="flex items-center -space-x-1.5 shrink-0">
                {boardUsers.slice(0, 3).map((user) => (
                  <div key={user.id} className="transition-transform hover:scale-110 hover:z-10">
                    <UserAvatar user={user} size="small" showTooltip={true} avatarClassName="ring-2 ring-card cursor-default" />
                  </div>
                ))}
                {boardUsers.length > 3 && (
                  <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium text-foreground ring-2 ring-card">+{boardUsers.length - 3}</div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    },
    [getTaskBoardUsers, kanban?.getActiveConfig, project]
  );

  const handleKanbanItemDblClick = useCallback(
    (record) => {
      entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: record.id });
    },
    [entityManager]
  );

  return (
    <>
      <ViewSpecificHeader
        viewToggleButtonConfig={{
          isKanbanMode: kanban.isKanbanMode,
          handleOnToggleView: kanban.handleOnToggleView,
          isGroupingApplied: kanban.isGroupingApplied,
          applyGroupingAndOpenKanban: kanban.applyGroupingAndOpenKanban,
          kanbanFieldConfig: kanban.kanbanFieldConfig,
          activeConfig: kanban.activeConfig,
          getActiveGroupOrder: kanban.getActiveGroupOrder
        }}
      />
      {user && <AlertNotificationBanner project={project} editorHandler={editorHandler} stateProvider={stateProvider} user={user} />}
      {bulkCopyRecords && (
        <CopyToBoardDialog
          open={!!bulkCopyRecords}
          onOpenChange={(isOpen) => { if (!isOpen) setBulkCopyRecords(null); }}
          records={bulkCopyRecords}
          entityType={ENTITY_TYPES.TASK}
        />
      )}
      <KanbanWrapper
        {...kanban}
        itemRenderer={kanbanItemRenderer}
        onItemDblClick={handleKanbanItemDblClick}
        grid={gridRef.current?.instance}
        processCellMenuItems={gridRef.current?.instance?.features?.cellMenu?.processItems}
      />
      <BryntumGridWrapper
        stateId
        ref={setRefs(gridRef)}
        flex="1 1 25%"
        onTaskFind={({ point }) => {
          editorHandler.openEditor(
            EDITOR_KEYS.taskFinderEditor,
            null,
            {
              point,
              onTaskClick: ({ record }) => {
                const originalRecord = getOriginalRecord(record);
                entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: originalRecord.id });
              }
            },
            {
              by: "recordType",
              useExisting: true
            }
          );
        }}
        searchFeature={true}
      />
    </>
  );
};

export default TaskPage;
